<template>
    <div>
        <ActivityPopUp
            style="z-index: 2000; position: absolute"
            :data="popupActivityData"
            :visible="isActivityPopupVisible"
            :position="popupPosition"
            :tab="tab"
            @close="closeActivityPopup"
            @add-filter="handleAddFilterPopUp"
            @delete-filter="handleDeleteFilterPopUp"
            @open-table-popup="openTablePopup"
        />
        <ActivityPopUpTables
            :visible="isTablePopupVisible"
            class="connections-popup"
            :data="data"
            :activityData="popupActivityData"
            @close="closeTablePopup"
        />
        <ArcsPopUp
            style="z-index: 2000; position: absolute"
            :data="popupArcData"
            :visible="isArcPopupVisible"
            :position="popupPosition"
            :tab="tab"
            @close="closeArcPopup"
            @add-filter="handleAddFilterPopUp"
            @delete-filter="handleDeleteFilterPopUp"
        />
        <div
            id="cy"
            ref="cyContainer"
            class="Graph"
            :style="cyContainerStyle"
        />
        <div
            v-if="unrenderableChart"
            class="rounded-borders border-warning text-warning flex items-center unrenderable"
        >
            <q-icon
                name="warning"
                class="q-mr-sm"
            />
            <div>
                {{ $t('visualization.complexModel') }}
            </div>
        </div>
        <q-inner-loading :showing="!data || data.activities?.length === 0 || Object.keys(data).length === 0 || variantsLoading" />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import cytoscape from 'cytoscape'
import dagre from 'cytoscape-dagre'
import klay from 'cytoscape-klay'
import { mediaQueryMixin } from '@/mixins'
import {
    Api, apiRequest, notifyError,
} from '@/api'
import filtersStorageMixin from '../../mixins/filtersStorage.mixin'
import nameFormatter from './cy/nameFormatter'
import Utils from './components/utils'
import graphCytoscapeStyles from './cy/styles'
import applyGraphSemanticStyles from './cy/styleEngine'
import TOKENS from './cy/tokens'
import ActivityPopUp from './components/ActivityPopUp.vue'
import ArcsPopUp from './components/ArcsPopUp.vue'
import ActivityPopUpTables from './components/ActivityPopUpTables.vue'
import { calcMinPath as calcMinPathUtil, calcMinimumPercent } from './utils/frequencyIndicator'

const colorMap = {
    START: TOKENS.node.hoverStartBg,
    END: TOKENS.node.hoverEndBg,
}

const MAX_ACTIVITIES = 125
const MAX_ARCS = 1000

const FIT_PADDING = 40

export default {
    name: 'GraphCyTo',
    components: {
        ActivityPopUp,
        ArcsPopUp,
        ActivityPopUpTables,
    },
    mixins: [mediaQueryMixin, filtersStorageMixin],
    inject: ['App'],
    props: {
        data: VueTypes.shape({
            activities: VueTypes.any,
            arcs: VueTypes.any,
        }).loose,
        model: VueTypes.shape({
            values: VueTypes.string,
            statsValue: VueTypes.string,
            directionValue: VueTypes.oneOf(['LR', 'TB']).def('TB'),
            heatmapMode: VueTypes.oneOf(['OFF', 'FREQUENCY', 'DURATION']).def('OFF'),
        }).loose,
        color: VueTypes.shape({
            activities: VueTypes.any,
            arcs: VueTypes.any,
        }).loose,
        parent: VueTypes.string,
        simple: VueTypes.bool.def(false),
        variantOverview: VueTypes.bool.def(false),
        variantsLoading: VueTypes.bool.def(false),
        tab: VueTypes.string,
    },
    emits: ['add-filter', 'delete-filter', 'frequency-indicator-state-changed'],
    data() {
        return {
            cy: null,
            popupActivityData: {},
            popupArcData: {},
            isActivityPopupVisible: false,
            isArcPopupVisible: false,
            isTablePopupVisible: false,
            popupPosition: { x: 0, y: 0 },
            tablePopupPosition: { x: 1, y: 1 },
            selectedActivityPopup: null,
            selectedArcPopup: null,
            nodesPosition: {},
            minPath: [],
            totalPath: [],
            validKnobValues: [],
            percentageMap: {},
            actualPercentIndex: 0,
            minPercent: 0,
            frequencyIndicatorValue: 100,
            frequencyIndicatorMinValue: 0,
            newData: null,
            screenContainerObserver: undefined,
            graphContainerObserver: undefined,
            resizeTimeout: null,
            fitTimeout: null,
            pageObserveTimeout: null,
            graphObserveTimeout: null,
            unrenderableChart: false,
            graphReady: false,
        }
    },
    computed: {
        cyContainerStyle() {
            return {
                width: 'auto',
                opacity: this.graphReady ? 1 : 0,
                pointerEvents: this.graphReady ? 'auto' : 'none',
            }
        },
    },
    watch: {
        data() {
            this.graphReady = false
            this.scheduleFit()
            this.newData = null
        },
    },

    created() {
        cytoscape.use(dagre)
        cytoscape.use(klay)
        cytoscape.warnings(false) // Disable warnings in console
    },

    mounted() {
        this.$watch((vm) => JSON.stringify([vm.data, vm.model?.values, vm.model?.statsValue, vm.model?.directionValue, vm.model?.heatmapMode]), () => {
            this.fetchGraph()
        }, { immediate: true })
        this.$watch(
            (vm) => JSON.stringify([vm.color, vm.model?.values, vm.model?.statsValue, vm.model?.directionValue, vm.model?.heatmapMode]),
            this.colorLoops,
            { immediate: true },
        )
        if (typeof ResizeObserver === 'function') {
            this.screenContainerObserver = new ResizeObserver(this.onResize)
        }
        this.pageObserveTimeout = setTimeout(() => {
            // Observe the closest page container for this graph.
            const closestPage = this.$el?.closest?.('.q-page')
            const pageEl = closestPage || document.querySelector('.q-page')
            if (pageEl && this.screenContainerObserver) {
                this.screenContainerObserver.observe(pageEl)
            }
        }, 50)

        // Resize/fit the graph when its own container gets its final size.
        if (typeof ResizeObserver === 'function') {
            this.graphContainerObserver = new ResizeObserver(this.onGraphContainerResize)
        }
        this.graphObserveTimeout = setTimeout(() => {
            if (this.$refs.cyContainer && this.graphContainerObserver) {
                this.graphContainerObserver.observe(this.$refs.cyContainer)
            }
        }, 0)
    },
    beforeUnmount() {
        if (this.pageObserveTimeout) {
            clearTimeout(this.pageObserveTimeout)
            this.pageObserveTimeout = null
        }
        if (this.graphObserveTimeout) {
            clearTimeout(this.graphObserveTimeout)
            this.graphObserveTimeout = null
        }
        if (this.screenContainerObserver) {
            this.screenContainerObserver.disconnect()
            this.screenContainerObserver = undefined
        }
        if (this.graphContainerObserver) {
            this.graphContainerObserver.disconnect()
            this.graphContainerObserver = undefined
        }
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout)
            this.resizeTimeout = null
        }
        if (this.fitTimeout) {
            clearTimeout(this.fitTimeout)
            this.fitTimeout = null
        }
        if (this.cy) {
            this.cy.off('mouseover', 'node')
            this.cy.off('mouseout', 'node')
            this.cy.off('mouseover', 'edge')
            this.cy.off('mouseout', 'edge')
            this.cy.off('dragfree', 'node')
            this.cy.off('tap', 'node')
            this.cy.off('tap', 'edge')
            this.cy.destroy()
            this.cy = null
        }
    },
    methods: {
        increaseZoom() {
            if (!this.cy) return
            const current = this.cy.zoom()
            const max = 2.5
            const next = Math.min(current + 0.1, max)
            this.cy.zoom(next)
            this.cy.center()
            this.$emit('zoom-changed', Math.round(next * 100))
        },
        decreaseZoom() {
            if (!this.cy) return
            const current = this.cy.zoom()
            const min = 0.1
            const next = Math.max(current - 0.1, min)
            this.cy.zoom(next)
            this.cy.center()
            this.$emit('zoom-changed', Math.round(next * 100))
        },
        emitFrequencyIndicatorStateChanged() {
            this.$emit('frequency-indicator-state-changed', this.getFrequencyIndicatorState())
        },
        emitZoomStateChanged() {
            this.$emit('zoom-state-changed', this.getZoomState())
        },
        getFrequencyIndicatorState() {
            const visible = !this.variantOverview
            const canIncrease = this.actualPercentIndex > 0
            const canDecrease = this.actualPercentIndex < this.validKnobValues.length - 1
            return {
                visible,
                value: this.frequencyIndicatorValue,
                minValue: this.frequencyIndicatorMinValue,
                canIncrease,
                canDecrease,
            }
        },
        getZoomState() {
            const zoomValue = this.cy ? Math.round(this.cy.zoom() * 100) : 100
            const canIncrease = zoomValue < 250
            const canDecrease = zoomValue > 10
            return {
                canIncrease,
                canDecrease,
            }
        },
        increaseFrequencyIndicator() {
            this.addLessFreqActivities()
        },

        decreaseFrequencyIndicator() {
            this.removeLessFreqActivities()
        },
        sanitizeLabel(value, { preserveNewlines = false } = {}) {
            const safeValue = value === null || value === undefined ? '' : String(value)
            const withoutHtml = safeValue.replace(/<[^>]*>/g, '')
            if (preserveNewlines) {
                return withoutHtml.replace(/\r\n/g, '\n')
            }
            return withoutHtml.replace(/\n/g, ' ')
        },

        formatEdgeLabel(statistics) {
            const raw = Utils.getInfoModel(statistics, this.model)

            const isBoth = this.model?.values === Utils.graphValues.BOTH
            const isLR = this.model?.directionValue === 'LR'

            if (!isBoth || !isLR) {
                return this.sanitizeLabel(raw)
            }

            // LR: show frequency on first line and duration on second.
            const frequency = statistics?.frequency ?? ''
            const durationSeconds = Utils.getStatValue(statistics?.duration, this.model?.statsValue)
            const durationText = Utils.normalizeDuration(durationSeconds)

            if (!durationText) return this.sanitizeLabel(frequency, { preserveNewlines: true })
            return this.sanitizeLabel(`${frequency}\n${durationText}`, { preserveNewlines: true })
        },

        scheduleFit() {
            if (this.fitTimeout) clearTimeout(this.fitTimeout)
            this.fitTimeout = setTimeout(() => {
                if (!this.cy) return
                const el = this.$refs.cyContainer
                const width = el?.clientWidth || 0
                const height = el?.clientHeight || 0
                if (width <= 0 || height <= 0) return

                this.cy.resize()
                this.cy.fit(this.cy.elements(), FIT_PADDING)
                this.graphReady = true
            }, 50)
        },
        onGraphContainerResize() {
            this.scheduleFit()
        },
        onResize() {
            // Container size can change with layout/drawers; keep cy in sync.
            this.scheduleFit()
        },
        fetchGraph(newData) {
            this.unrenderableChart = false
            this.graphReady = false
            if (newData) this.newData = this.formatData(newData)
            if (!this.renderGraph()) {
                this.unrenderableChart = true
                return
            }
            this.adjustNodeWithContent()
            if (this.model && (this.data || this.newData)) this.enableLoopsAndOverlaps(this.newData || this.data, this.model)
            this.adjustSelfLoops()
            this.applyLayout()
            this.scheduleFit()
            if (this.handleSessionStorage('get')) this.applyNodesPositionByVariant(this.handleSessionStorage('get'))
            this.resetFreqIndictadorValues()
            if (!this.variantOverview) this.calcMinPath()
            this.emitFrequencyIndicatorStateChanged()
        },
        renderGraph() {
            if (this.cy) {
                this.cy.destroy()
                this.cy = null
            }
            this.cy = cytoscape({
                container: this.$refs.cyContainer,
                style: graphCytoscapeStyles,
                wheelSensitivity: 0.25,
                maxZoom: 2.5,
                pixelRatio: 2,
            })
            // Charging the data if exists
            if (this.newData || this.data) {
                const { activities = [], arcs = [] } = this.newData || this.data
                if (activities.length < MAX_ACTIVITIES && arcs.length < MAX_ARCS) {
                    this.cy.add(this.renderActivities())
                    this.cy.add(this.renderArcs())
                } else {
                    return false
                }
            }
            this.bindCyEvents()
            return true
        },

        bindCyEvents() {
            if (!this.cy) return

            // Defensive: ensure we never accumulate duplicate handlers on the same cy instance.
            this.cy.off('mouseover', 'node')
            this.cy.off('mouseout', 'node')
            this.cy.off('mouseover', 'edge')
            this.cy.off('mouseout', 'edge')
            this.cy.off('dragfree', 'node')
            this.cy.off('tap', 'node')
            this.cy.off('tap', 'edge')

            this.listenNodeMove()

            if (!this.variantOverview) {
                this.openNodePopup()
                this.openArcPopup()
            }
        },

        applyLayout() {
            const direction = this.model.directionValue

            this.runDagreLayout(direction)

            // If Dagre leaves overlaps, try a more relaxed Dagre pass.
            let overlaps = this.detectOverlappingNodes()
            if ((overlaps?.nodes || []).length > 0) {
                this.runDagreLayout(direction, { relaxed: true })
                overlaps = this.detectOverlappingNodes()
            }

            // Keep Klay as a safe fallback for highly complex graphs.
            if ((overlaps?.nodes || []).length > 0) {
                this.runKlayLayout(direction, { relaxed: true })
            }

            this.adjustZoomByNodes()
            this.setEndStartNodes(direction)
            this.straightenSinglePath(direction)
            this.saveInitialPosition()
        },

        calculateDagreSpacing(modelDirection, { relaxed = false } = {}) {
            const edgeCount = this.cy.edges().length
            const nodeCount = this.cy.nodes().length
            const tinyGraph = edgeCount < 40
            const mediumGraph = edgeCount >= 40 && edgeCount < 150
            const greatGraph = edgeCount >= 150
            const nodeWidths = this.cy.nodes().map((node) => node.width()).filter((width) => Number.isFinite(width) && width > 0)
            const nodeHeights = this.cy.nodes().map((node) => node.height()).filter((height) => Number.isFinite(height) && height > 0)

            const averageWidth = nodeWidths.length > 0
                ? nodeWidths.reduce((total, width) => total + width, 0) / nodeWidths.length
                : 120
            const averageHeight = nodeHeights.length > 0
                ? nodeHeights.reduce((total, height) => total + height, 0) / nodeHeights.length
                : 56
            const containerCrossAxisBoost = this.calculateDagreCrossAxisContainerBoost({
                modelDirection,
                nodeCount,
                crossAxisAverage: modelDirection === 'TB' ? averageWidth : averageHeight,
                mediumGraph,
                greatGraph,
            })

            let nodeSep = tinyGraph ? 28 : 38
            let rankSep = tinyGraph ? 42 : 56
            let edgeSep = tinyGraph ? 10 : 14

            if (mediumGraph) {
                nodeSep = 42
                rankSep = 64
                edgeSep = 16
            }

            if (greatGraph) {
                nodeSep = 48
                rankSep = 72
                edgeSep = 18
            }

            const crossAxisAverage = modelDirection === 'TB' ? averageWidth : averageHeight
            const mainAxisAverage = modelDirection === 'TB' ? averageHeight : averageWidth
            const { rankSepBoost, edgeSepBoost } = this.calculateDagreEdgeLabelSpacing(modelDirection)

            nodeSep = Math.max(nodeSep, Math.round(crossAxisAverage * 0.22))
            nodeSep += containerCrossAxisBoost
            rankSep = Math.max(rankSep, Math.round(mainAxisAverage * 0.72))
            rankSep += rankSepBoost
            edgeSep += edgeSepBoost

            if (this.model?.values === Utils.graphValues.BOTH && modelDirection === 'LR') {
                rankSep += 8
            }

            const compactFactor = this.calculateDagreCompactFactor({
                modelDirection,
                edgeCount,
                nodeCount,
                greatGraph,
                mediumGraph,
            })

            if (compactFactor < 1) {
                nodeSep = Math.max(20, Math.round(nodeSep * compactFactor))
                rankSep = Math.max(36, Math.round(rankSep * compactFactor))
                edgeSep = Math.max(8, Math.round(edgeSep * compactFactor))
            }

            if (relaxed) {
                nodeSep = Math.round(nodeSep * 1.3)
                rankSep = Math.round(rankSep * 1.3)
                edgeSep = Math.round(edgeSep * 1.2)
            }

            return { nodeSep, rankSep, edgeSep }
        },

        calculateDagreCrossAxisContainerBoost({
            modelDirection,
            nodeCount,
            crossAxisAverage,
            mediumGraph,
            greatGraph,
        }) {
            const container = this.$refs.cyContainer
            const crossAxisSize = modelDirection === 'LR' ? container?.clientHeight || 0 : container?.clientWidth || 0

            if (crossAxisSize <= 0 || nodeCount <= 2) {
                return 0
            }

            const estimatedNodesPerBand = Math.max(2, Math.min(nodeCount, Math.round(Math.sqrt(nodeCount))))
            const availableBandSize = crossAxisSize / estimatedNodesPerBand
            const desiredBandSize = crossAxisAverage + 28
            const freeCrossAxisSpace = availableBandSize - desiredBandSize
            const isLR = modelDirection === 'LR'

            if (freeCrossAxisSpace <= 0) {
                return 0
            }

            let maxBoost = isLR ? 28 : 22
            if (mediumGraph) {
                maxBoost = isLR ? 24 : 18
            }
            if (greatGraph) {
                maxBoost = 12
            }

            let boostFactor = 0.32
            if (isLR && !greatGraph) {
                boostFactor = mediumGraph ? 0.42 : 0.5
            }

            return Math.max(0, Math.min(maxBoost, Math.round(freeCrossAxisSpace * boostFactor)))
        },

        calculateDagreCompactFactor({
            modelDirection,
            edgeCount,
            nodeCount,
            mediumGraph,
            greatGraph,
        }) {
            if (nodeCount <= 3) {
                return 1
            }

            let factor = modelDirection === 'LR' ? 0.84 : 0.9
            const density = edgeCount / Math.max(1, nodeCount - 1)

            if (mediumGraph) {
                factor -= 0.04
            }

            if (greatGraph) {
                factor -= 0.06
            }

            if (density > 1.4) {
                factor += 0.06
            }

            return Math.max(0.76, Math.min(1, factor))
        },

        calculateDagreEdgeLabelSpacing(modelDirection) {
            if (modelDirection !== 'LR') {
                return {
                    rankSepBoost: 0,
                    edgeSepBoost: 0,
                }
            }

            const edges = this.cy.edges()
            let multilineEdges = 0
            let maxLines = 1
            let widestLineLength = 0

            edges.forEach((edge) => {
                const label = String(edge.data('label') || '')
                if (!label) return

                const lines = label.split('\n')
                maxLines = Math.max(maxLines, lines.length)
                widestLineLength = Math.max(widestLineLength, ...lines.map((line) => line.length))

                if (lines.length > 1) {
                    multilineEdges += 1
                }
            })

            if (multilineEdges === 0) {
                return {
                    rankSepBoost: 0,
                    edgeSepBoost: 0,
                }
            }

            const rankSepBoost = Math.min(12, ((maxLines - 1) * 4) + (widestLineLength > 14 ? 2 : 0))
            const edgeSepBoost = Math.min(4, multilineEdges > 2 ? 4 : 2)

            return {
                rankSepBoost,
                edgeSepBoost,
            }
        },

        determineDagreRanker() {
            const nodes = this.cy.nodes()
            const edges = this.cy.edges().filter((edge) => edge.source().id() !== edge.target().id())

            if (nodes.length <= 3 || edges.length <= 2) {
                return 'longest-path'
            }

            let branchingNodes = 0
            let mergedNodes = 0

            nodes.forEach((node) => {
                const outgoing = node.outgoers('edge').filter((edge) => edge.source().id() !== edge.target().id()).length
                const incoming = node.incomers('edge').filter((edge) => edge.source().id() !== edge.target().id()).length

                if (outgoing > 1) branchingNodes += 1
                if (incoming > 1) mergedNodes += 1
            })

            const mostlyTreeLike = branchingNodes <= 1 && mergedNodes <= 1 && edges.length <= nodes.length + 1
            if (mostlyTreeLike) {
                return 'tight-tree'
            }

            return 'network-simplex'
        },

        buildDagreSortOrder() {
            const nodes = this.cy.nodes()
            const adjacency = new Map()
            const inDegree = new Map()
            const orderMap = {}
            const pathPriority = {}
            const queued = new Set()
            const visited = new Set()
            let nextOrder = 0

            const compareNodes = (nodeA, nodeB) => {
                const priorityA = pathPriority[nodeA?.id()] || 0
                const priorityB = pathPriority[nodeB?.id()] || 0
                if (priorityA !== priorityB) {
                    return priorityB - priorityA
                }

                const freqA = Number(nodeA?.data('freq')) || 0
                const freqB = Number(nodeB?.data('freq')) || 0
                if (freqA !== freqB) {
                    return freqB - freqA
                }

                const rankA = nodeA?.data('rank') || 0
                const rankB = nodeB?.data('rank') || 0
                if (rankA !== rankB) {
                    return rankA - rankB
                }

                return String(nodeA?.id() || '').localeCompare(String(nodeB?.id() || ''))
            }

            const enqueue = (nodeId) => {
                if (!nodeId || queued.has(nodeId) || visited.has(nodeId)) return
                queued.add(nodeId)
                roots.push(nodeId)
            }

            nodes.forEach((node) => {
                adjacency.set(node.id(), [])
                inDegree.set(node.id(), 0)
                pathPriority[node.id()] = Number(node.data('freq')) || 0
            })

            this.cy.edges().forEach((edge) => {
                const sourceId = edge.source().id()
                const targetId = edge.target().id()
                if (sourceId === targetId) return
                if (!adjacency.has(sourceId) || !adjacency.has(targetId)) return

                adjacency.get(sourceId).push(targetId)
                inDegree.set(targetId, (inDegree.get(targetId) || 0) + 1)
            })

            const roots = []
            if (adjacency.has('START')) {
                enqueue('START')
            }

            nodes
                .filter((node) => (inDegree.get(node.id()) || 0) === 0 && node.id() !== 'START')
                .sort(compareNodes)
                .forEach((node) => enqueue(node.id()))

            while (roots.length > 0) {
                const nodeId = roots.shift()
                queued.delete(nodeId)
                if (!nodeId || visited.has(nodeId)) {
                    // Skip empty queue entries or already-processed nodes.
                } else {
                    visited.add(nodeId)
                    orderMap[nodeId] = nextOrder
                    nextOrder += 1

                    const children = (adjacency.get(nodeId) || [])
                        .map((childId) => this.cy.getElementById(childId))
                        .filter((childNode) => childNode && childNode.length > 0)
                        .sort(compareNodes)

                    children.forEach((childNode) => {
                        const childId = childNode.id()
                        const candidatePriority = (pathPriority[nodeId] || 0) + (Number(childNode.data('freq')) || 0)
                        pathPriority[childId] = Math.max(pathPriority[childId] || 0, candidatePriority)
                        const remaining = Math.max(0, (inDegree.get(childId) || 0) - 1)
                        inDegree.set(childId, remaining)
                        if (remaining === 0) {
                            enqueue(childId)
                        }
                    })
                }
            }

            nodes
                .filter((node) => !visited.has(node.id()))
                .sort((nodeA, nodeB) => {
                    const inDegreeA = inDegree.get(nodeA.id()) || 0
                    const inDegreeB = inDegree.get(nodeB.id()) || 0
                    if (inDegreeA !== inDegreeB) {
                        return inDegreeA - inDegreeB
                    }
                    return compareNodes(nodeA, nodeB)
                })
                .forEach((node) => {
                    orderMap[node.id()] = nextOrder
                    nextOrder += 1
                })

            return {
                orderMap,
                pathPriority,
            }
        },

        runDagreLayout(modelDirection, { relaxed = false } = {}) {
            const { nodeSep, rankSep, edgeSep } = this.calculateDagreSpacing(modelDirection, { relaxed })
            const ranker = this.determineDagreRanker()
            const { orderMap: sortOrder, pathPriority } = this.buildDagreSortOrder()

            const layout = this.cy.layout({
                name: 'dagre',
                fit: false,
                animate: false,
                nodeDimensionsIncludeLabels: true,
                rankDir: modelDirection === 'TB' ? 'TB' : 'LR',
                ranker,
                nodeSep,
                rankSep,
                edgeSep,
                padding: 20,
                sort: (a, b) => {
                    const orderA = sortOrder[a.id()] ?? Number.MAX_SAFE_INTEGER
                    const orderB = sortOrder[b.id()] ?? Number.MAX_SAFE_INTEGER
                    if (orderA !== orderB) {
                        return orderA - orderB
                    }

                    const priorityA = pathPriority[a.id()] || 0
                    const priorityB = pathPriority[b.id()] || 0
                    if (priorityA !== priorityB) {
                        return priorityB - priorityA
                    }

                    const freqA = Number(a.data('freq')) || 0
                    const freqB = Number(b.data('freq')) || 0
                    if (freqA !== freqB) {
                        return freqB - freqA
                    }

                    const rankA = a.data('rank') || 0
                    const rankB = b.data('rank') || 0
                    return rankA - rankB
                },
            })

            layout.run()
        },

        runKlayLayout(modelDirection, { relaxed = false } = {}) {
            const klayOptions = this.adjustNodeAndEdgeByDirection(modelDirection)
            const options = relaxed
                ? {
                    ...klayOptions,
                    spacing: Math.round((klayOptions.spacing || 20) * 1.4),
                    borderSpacing: Math.round((klayOptions.borderSpacing || 20) * 1.4),
                    thoroughness: Math.min(20, (klayOptions.thoroughness || 7) + 5),
                    edgeSpacingFactor: Math.min(1.2, (klayOptions.edgeSpacingFactor ?? 0.5) + 0.2),
                    inLayerSpacingFactor: Math.min(3, (klayOptions.inLayerSpacingFactor || 1) + 0.3),
                }
                : klayOptions

            const layout = this.cy.layout({
                name: 'klay',
                nodeDimensionsIncludeLabels: true,
                fit: false,
                animate: false,
                klay: options,
            })

            layout.run()
        },

        isSinglePathGraph() {
            if (!this.cy) return false

            const nodes = this.cy.nodes()
            if (nodes.length <= 2) return true

            const nonSelfLoopOutDegree = (node) => node.outgoers('edge')
                .filter((e) => e.source().id() !== e.target().id()).length
            const nonSelfLoopInDegree = (node) => node.incomers('edge')
                .filter((e) => e.source().id() !== e.target().id()).length

            for (let i = 0; i < nodes.length; i += 1) {
                const node = nodes[i]
                const id = node.id()
                const inDeg = nonSelfLoopInDegree(node)
                const outDeg = nonSelfLoopOutDegree(node)

                // Disallow isolated nodes (they tend to be layout artefacts/data issues)
                if (inDeg + outDeg === 0) return false

                if (id === 'START') {
                    if (inDeg !== 0 || outDeg > 1) return false
                } else if (id === 'END') {
                    if (outDeg !== 0 || inDeg > 1) return false
                } else if (inDeg > 1 || outDeg > 1) {
                    return false
                }
            }

            return true
        },

        straightenSinglePath(modelDirection) {
            if (!this.cy) return

            const isSinglePath = this.isSinglePathGraph()
            const nonLoopEdges = this.cy.edges().filter((e) => e.source().id() !== e.target().id())

            // Restore default edge curvature when not a simple path.
            if (!isSinglePath) {
                nonLoopEdges.style('curve-style', 'bezier')
                return
            }

            // For a single path, align nodes to the main axis and draw straight edges.
            const nodes = this.cy.nodes()
            const coreNodes = nodes.filter((n) => n.id() !== 'START' && n.id() !== 'END')
            if (modelDirection === 'TB') {
                const xs = (coreNodes.length > 0 ? coreNodes : nodes).map((n) => n.position('x'))
                const x = xs.reduce((acc, v) => acc + v, 0) / xs.length
                nodes.forEach((n) => n.position({ x, y: n.position('y') }))
            } else {
                const ys = (coreNodes.length > 0 ? coreNodes : nodes).map((n) => n.position('y'))
                const y = ys.reduce((acc, v) => acc + v, 0) / ys.length
                nodes.forEach((n) => n.position({ x: n.position('x'), y }))
            }

            nonLoopEdges.style('curve-style', 'straight')
        },
        renderActivities() {
            const { activities = [] } = this.newData || this.data
            const elements = []
            const maxFreq = Math.max(...activities.map((item) => item.frequency))
            activities.forEach((item, index) => {
                const statistics = {
                    duration: item.duration,
                    frequency: this.App.numberLocationFormat(item.frequency),
                    ...(item.attribute ? { attribute: item.attribute } : {}),
                }
                const strokeCalc = (item.frequency * 5) / maxFreq
                const maxStroke = 7
                const borderStroke = Math.min(strokeCalc < 1 ? 1.75 : strokeCalc, maxStroke)
                const propsData = {
                    id: `activity-${index}`,
                    name: item.name === 'START' || item.name === 'END' ? this.$t(`visualization.graph.${item.name}`) : item.name,
                    statistics,
                    model: this.model,
                    simple: this.simple,
                }
                const value = this.valueFormatter(Utils.getInfoModel(statistics, this.model))
                const info = `\t${propsData.name }\n\n${ value}`
                elements.push({
                    group: 'nodes',
                    data: {
                        id: item.name,
                        freq: item.frequency,
                        statistics,
                        model: this.model,
                        simple: this.simple,
                        content: info,
                        rank: index, // Now we rank by index, bcs its "always the same"
                        stroke: borderStroke,
                        bgColor: TOKENS.node.defaultBg,
                        textColor: '#000000',
                        modelData: {
                            variantCount: this.data?.variants,
                            variantUid: this.data?.variantId,
                            loopUid: this.data?.loopId,
                        },
                    },
                })
            })
            return elements
        },
        determineEdgeStyle(arc) {
            if (arc.sourceId === -1) {
                return 'start'
            }
            if (arc.targetId === -2) {
                return 'end'
            }
            return 'normal'
        },
        renderArcs() {
            const { arcs = [] } = this.newData || this.data
            const maxFreq = Math.max(...arcs.map((item) => item.frequency))
            const elements = []
            arcs.forEach((item, index) => {
                let cyclesEdge = 0
                const id = `arc-node-${index}`
                const maxStroke = 7
                const styleClass = this.determineEdgeStyle(item)
                const strokeSize = (item.frequency * 5) / maxFreq
                const stroke = Math.min(strokeSize < 1 ? 1.75 : strokeSize, maxStroke)
                const statistics = {
                    duration: item.duration,
                    frequency: this.App.numberLocationFormat(item.frequency),
                    overlap: true,
                }
                const arcValue = Utils.getInfoModel(statistics, this.model)
                if (item.source === item.target) {
                    cyclesEdge = this.sanitizeLabel(arcValue)
                }
                if (!this.checkInconsistencyArcs(item)) {
                    console.error('Inconsistency in data received from Arcs model ->', id, "doesn't exist in nodes")
                    return
                }
                elements.push({
                    group: 'edges',
                    classes: styleClass,
                    data: {
                        id: `arc-node-${nameFormatter(item.name)}`,
                        name: item.name,
                        freq: item.frequency,
                        source: item.source,
                        target: item.target,
                        label: this.formatEdgeLabel(statistics),
                        statistics,
                        lineColor: TOKENS.edge.normalLine,
                        strokeWidth: stroke,
                        cycles: cyclesEdge,
                        modelData: {
                            variantCount: this.data?.variants,
                            variantUid: this.data?.variantId,
                            loopUid: this.data?.loopId,
                        },
                    },
                })
            })
            return elements
        },

        checkInconsistencyArcs(arc) {
            if (this.cy.getElementById(arc.source).length === 0 || this.cy.getElementById(arc.target).length === 0) {
                return false
            }
            return true
        },

        valueFormatter(value) {
            // Backwards-compatible sanitizer for node labels/content.
            return this.sanitizeLabel(value)
        },

        adjustNodeWithContent() {
            this.cy.nodes().forEach((node) => {
                const canvasTemp = document.createElement('canvas')
                const ctx = canvasTemp.getContext('2d')
                const counter = node.data('content') || ''
                // Match the Cytoscape stylesheet (font-size 18px, font-weight 500)
                ctx.font = '500 18px "Open Sans"'
                const textWidth = ctx.measureText(String(counter)).width
                // Add a small buffer to avoid subpixel/font-load clipping.
                node.style('width', `${Math.ceil(textWidth + 14)}px`)
                node.style('height', 'auto')
                node.style('padding', `2px`)
            })
        },

        adjustNodeAndEdgeByDirection(modelDirection) {
            const tinyGraph = this.cy.edges().length < 40
            const mediumGraph = this.cy.edges().length > 40 && this.cy.edges().length < 150
            const greatGraph = this.cy.edges().length > 150
            const options = {
                layoutHierarchy: true,
                thoroughness: greatGraph ? 14 : 12,
                compactComponents: false,
                mergeEdges: true,
                separateConnectedComponents: true,
                edgeRouting: 'SPLINES',
                crossingMinimization: 'LAYER_SWEEP',
                cycleBreaking: 'GREEDY',
                fixedAlignment: 'BALANCED',
                mergeHierarchyCrossingEdges: false,
                addUnnecessaryBendpoints: false,
                randomizationSeed: 1,
                borderSpacing: greatGraph ? 40 : 30,
                nodeLayering: 'NETWORK_SIMPLEX',
                nodePlacement: 'BRANDES_KOEPF',
            }
            if (modelDirection === 'TB') {
                options.direction = 'DOWN'
                if (tinyGraph) {
                    options.edgeSpacingFactor = 0.35
                    options.inLayerSpacingFactor = 1.4
                    options.spacing = 40
                }
                if (mediumGraph) {
                    options.edgeSpacingFactor = 0.3
                    options.inLayerSpacingFactor = 1.3
                    options.spacing = 55
                }
                if (greatGraph) {
                    options.edgeSpacingFactor = 0.4
                    options.inLayerSpacingFactor = 1.5
                    options.spacing = 70
                }
            } else {
                options.direction = 'RIGHT'
                if (tinyGraph) {
                    options.edgeSpacingFactor = 0.3
                    options.inLayerSpacingFactor = 1.6
                    options.spacing = 55
                }
                if (mediumGraph) {
                    options.edgeSpacingFactor = 0.3
                    options.inLayerSpacingFactor = 1.5
                    options.spacing = 65
                }
                if (greatGraph) {
                    options.edgeSpacingFactor = 0.4
                    options.inLayerSpacingFactor = 1.6
                    options.spacing = 80
                }

                // When showing both metrics and drawing LR, edge labels become taller (two lines).
                // Add the smallest spacing bump to reduce label overlap with adjacent nodes.
                if (this.model?.values === Utils.graphValues.BOTH) {
                    options.spacing += 12
                    options.borderSpacing = Math.max(options.borderSpacing || 0, 36)
                }
            }
            return options
        },

        // System of detect overlapping nodes
        detectOverlappingNodes() {
            const nodes = this.cy.nodes()
            const overlappingPairs = {
                nodes: [],
                topNode: null,
            }
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < nodes.length; i++) {
                // eslint-disable-next-line no-plusplus
                for (let j = i + 1; j < nodes.length; j++) {
                    const nodeA = nodes[i]
                    const nodeB = nodes[j]

                    const posA = nodeA.position()
                    const posB = nodeB.position()

                    const widthA = nodeA.width()
                    const widthB = nodeB.width()
                    const heightA = nodeA.height()
                    const heightB = nodeB.height()

                    if (Math.abs(posA.x - posB.x) < (widthA + widthB) / 2
                        && Math.abs(posA.y - posB.y) < (heightA + heightB) / 2) {
                        overlappingPairs.nodes.push([nodeA.data('id'), nodeB.data('id')])
                        overlappingPairs.topNode = this.cy.elements().indexOf(nodeA) > this.cy.elements().indexOf(nodeB) ? nodeA.data('id') : nodeB.data('id')
                    }
                }
            }

            return overlappingPairs
        },

        adjustSelfLoops() {
            this.cy.edges().forEach((edge) => {
                const source = edge.source()
                const target = edge.target()
                if (source.data('id') === target.data('id')) {
                    const nodeWidth = parseFloat(source.style('width')) / 2.2
                    const newControlPointStepSize = nodeWidth < 100 ? 100 : nodeWidth
                    edge.style('control-point-step-size', newControlPointStepSize)
                }
            })
        },

        adjustZoomByNodes() {
            if (this.cy.nodes().length <= 5) {
                this.cy.zoom({ level: 1.1 })
                this.cy.center()
            } else {
                this.cy.center()
            }
            this.emitZoomStateChanged()
        },

        enableLoopsAndOverlaps(data, model, onlyColor) {
            applyGraphSemanticStyles({
                cy: this.cy,
                graphData: data,
                model,
                onlyColor: !!onlyColor,
            })
        },

        colorLoops() {
            if (this.color !== undefined) {
                this.enableLoopsAndOverlaps(this.color, this.model, true)
            }
        },

        centerGraph() {
            this.resetInitialPosition()
            this.cy.fit(this.cy.elements(), FIT_PADDING)
            this.emitZoomStateChanged()
            this.handleSessionStorage('remove')
        },

        saveInitialPosition() {
            this.cy.nodes().forEach((node) => {
                node.data('initialPosition', { x: node.position().x, y: node.position().y })
            })
        },

        resetInitialPosition() {
            this.cy.nodes().forEach((node) => {
                const originalPosition = node.data('initialPosition')
                node.position({
                    x: originalPosition.x,
                    y: originalPosition.y,
                })
            })
        },

        downloadGraph(format) {
            this.downloadPNGorJPG(format)
        },

        calculateScale(variants) {
            const maxScale = 2.0
            const minScale = 0.33
            const threshold = 10000

            const variantCount = Number(variants)
            if (!Number.isFinite(variantCount) || variantCount <= 0) {
                return 1
            }

            if (variantCount > threshold) {
                return minScale
            }
            const ratio = variantCount / threshold

            const scaled = maxScale - (maxScale - minScale) * (ratio ** 0.2)
            return Math.max(minScale, Math.min(maxScale, scaled))
        },

        async downloadPNGorJPG(format) {
            if (!this.cy) return

            const elementCount = this.cy.elements().length
            if (elementCount === 0) return

            const type = String(format?.value || 'PNG').toUpperCase()
            const ext = type === 'PNG' ? 'png' : 'jpg'

            const link = document.createElement('a')
            link.download = `Graph.${ext}`
            link.style.display = 'none'

            const scale = this.calculateScale(this.data?.variants)

            // Keep these within common browser canvas limits.
            const maxWidth = 16384
            const maxHeight = 16384

            try {
                const baseOptions = {
                    full: true,
                    scale,
                    maxWidth,
                    maxHeight,
                    output: 'blob-promise',
                }

                const blob = type === 'PNG'
                    ? await this.cy.png(baseOptions)
                    : await this.cy.jpg({ ...baseOptions, quality: 0.92 })

                if (!blob || blob.size === 0) return

                const url = URL.createObjectURL(blob)
                link.href = url
                document.body.appendChild(link)
                link.click()
                link.remove()
                URL.revokeObjectURL(url)
            } catch (error) {
                // Fallback: try base64uri for smaller exports.
                try {
                    const href = type === 'PNG'
                        ? this.cy.png({
                            full: true,
                            scale,
                            maxWidth,
                            maxHeight,
                        })
                        : this.cy.jpg({
                            full: true,
                            scale,
                            maxWidth,
                            maxHeight,
                            quality: 0.92,
                        })
                    if (!href || href === 'data:,') return
                    link.href = href
                    document.body.appendChild(link)
                    link.click()
                    link.remove()
                } catch (innerError) {
                    console.error('Graph export failed', error, innerError)
                }
            }
        },

        listenNodeHover() {
            const clearNodeHoverState = () => {
                if (!this.cy) return
                this.cy.nodes().forEach((n) => {
                    n.removeClass('is-hover')
                    n.removeData('hoverColor')
                })
            }

            this.cy.on('mouseover', 'node', (event) => {
                const node = event.target
                const name = node.data('id')

                clearNodeHoverState()

                const highlightColor = node.hasClass('is-loop')
                    ? TOKENS.node.hoverLoopBg
                    : (colorMap[name] || TOKENS.node.hoverDefaultBg)

                node.data('hoverColor', highlightColor)
                node.addClass('is-hover')
            })

            this.cy.on('mouseout', 'node', () => {
                clearNodeHoverState()
            })

            // When leaving a node to empty canvas, ensure hover styles are fully reset.
            this.cy.on('mouseover', (event) => {
                if (event.target === this.cy) {
                    clearNodeHoverState()
                }
            })

            this.cy.on('mouseout', (event) => {
                if (event.target === this.cy) {
                    clearNodeHoverState()
                }
            })
        },

        saveNodesPositionByVariant() {
            const nodes = this.cy.nodes()
            nodes.forEach((node) => {
                const name = node.data('id')
                const position = node.position()
                this.nodesPosition[name] = position
            })
            this.handleSessionStorage('set')
        },

        handleSessionStorage(op) {
            const { processId } = this.$route.params
            const variant = this.data?.variantId || this.data?.variants
            const orientation = this.model?.directionValue
            const key = JSON.stringify({ processId, variant, orientation })
            const info = JSON.stringify({ position: this.nodesPosition })
            switch (op) {
                case 'get':
                    return sessionStorage.getItem(key)
                case 'set':
                    return sessionStorage.setItem(key, info)
                case 'remove':
                    return sessionStorage.removeItem(key)
                default:
                    return sessionStorage.getItem(key)
            }
        },

        applyNodesPositionByVariant(positions) {
            const nodes = this.cy.nodes()
            nodes.forEach((node) => {
                const name = node.data('id')
                const position = JSON.parse(positions).position[name]
                if (position) {
                    node.position(position)
                }
            })
        },
        listenEdgeHover() {
            this.cy.on('mouseover', 'edge', (event) => {
                const edge = event.target
                edge.addClass('is-hover')
            })
            this.cy.on('mouseout', 'edge', (event) => {
                const edge = event.target
                edge.removeClass('is-hover')
            })
        },

        listenNodeMove() {
            this.cy.on('dragfree', 'node', () => {
                this.saveNodesPositionByVariant()
            })
        },

        setEndStartNodes(modelDirection) {
            const startNode = this.cy.getElementById('START')
            const endNode = this.cy.getElementById('END')
            if (!startNode || !endNode) return

            const otherNodes = this.cy.nodes().filter((n) => n.id() !== 'START' && n.id() !== 'END')
            if (otherNodes.length === 0) return

            const bb = otherNodes.boundingBox()
            const centerX = (bb.x1 + bb.x2) / 2
            const centerY = (bb.y1 + bb.y2) / 2

            if (modelDirection === 'TB') {
                const margin = Math.max(140, startNode.height(), endNode.height())
                startNode.position({ x: centerX, y: bb.y1 - margin })
                endNode.position({ x: centerX, y: bb.y2 + margin })
            } else {
                const margin = Math.max(160, startNode.width(), endNode.width())
                startNode.position({ x: bb.x1 - margin, y: centerY })
                endNode.position({ x: bb.x2 + margin, y: centerY })
            }
        },

        // POPUPs METHODS

        openNodePopup() {
            this.cy.on('tap', 'node', (event) => {
                const node = event.target
                if (node.data('id') === 'START'
                    || node.data('id') === 'END') return
                if (node.data('id') === this.selectedActivityPopup) {
                    this.closeActivityPopup()
                    return
                }
                this.closeArcPopup()
                // Keep Cytoscape classes as source of truth: clear prior selection deterministically.
                const pos = this.calcPositionPopup(node)

                this.popupActivityData = node.data()
                this.popupPosition = {
                    x: pos.x,
                    y: pos.y,
                }
                event.stopPropagation()
                this.isActivityPopupVisible = true
                this.selectedActivityPopup = node.data('id')
            })
            // If user changes variant of the graph
            if (this.isActivityPopupVisible && this.selectedActivityPopup) {
                const node = this.cy.getElementById(this.selectedActivityPopup)
                if (node.data() === undefined) {
                    this.closeActivityPopup()
                } else {
                    this.popupActivityData = node.data()
                    const newPos = this.calcPositionPopup(node)
                    this.popupPosition = {
                        x: newPos.x,
                        y: newPos.y,
                    }
                }
            }
        },

        openArcPopup() {
            this.cy.on('tap', 'edge', (event) => {
                const edge = event.target
                if (edge.data('source') === 'START' || edge.data('target') === 'END') return
                if (edge.data('id') === this.selectedArcPopup) {
                    this.closeArcPopup()
                    return
                }
                this.closeActivityPopup()
                // Keep Cytoscape classes as source of truth: clear prior selection deterministically.
                const pos = this.calcPositionPopup(edge)
                this.popupArcData = edge.data()
                this.popupPosition = {
                    x: pos.x,
                    y: pos.y,
                }
                this.isArcPopupVisible = true
                this.selectedArcPopup = edge.data('id')
            })
            // If user changes variant of the graph
            if (this.isArcPopupVisible && this.selectedArcPopup) {
                const edge = this.cy.getElementById(this.selectedArcPopup)
                if (edge.data() === undefined) {
                    this.closeArcPopup()
                } else {
                    this.popupArcData = edge.data()
                    const newPos = this.calcPositionPopup(edge)
                    this.popupPosition = {
                        x: newPos.x,
                        y: newPos.y,
                    }
                }
            }
        },

        calcPositionPopup(el) {
            const elementBox = el.renderedBoundingBox()
            const popupHeight = 390
            const popupWidth = 330
            const offsetX = 80
            const offsetY = 40
            const container = document.querySelector('.ChartContainer')
            const visibleAreaHeight = container.getBoundingClientRect().height
            let popupX = elementBox.x1 + (elementBox.x2 - elementBox.x1) / 2 + offsetX
            let popupY = elementBox.y1 + (elementBox.y2 - elementBox.y1) / 2 - offsetY

            if (popupX + popupWidth > container.getBoundingClientRect().width) {
                popupX = elementBox.x1 - popupWidth - offsetX
            } else if (popupX < 0) {
                popupX = elementBox.x2 + offsetX
            }

            popupX = Math.max(0, Math.min(popupX, container.getBoundingClientRect().width - offsetX))

            if (popupY + popupHeight > visibleAreaHeight) {
                popupY = elementBox.y1 - popupHeight - offsetY
            } else if (popupY < popupHeight / 2) {
                popupY = elementBox.y2 + offsetY
            }
            popupY = Math.max(0, Math.min(popupY, visibleAreaHeight - popupHeight))

            return {
                x: popupX,
                y: popupY,
            }
        },

        closeActivityPopup() {
            this.isActivityPopupVisible = false
            this.selectedActivityPopup = null
            this.popupPosition = null
        },
        closeArcPopup() {
            this.isArcPopupVisible = false
            this.selectedArcPopup = null
            this.popupPosition = null
        },
        closeTablePopup() {
            this.isTablePopupVisible = false
            this.tablePopupPosition = null
        },
        handleAddFilterPopUp(filter) {
            this.$emit('add-filter', filter)
        },
        handleDeleteFilterPopUp(filter) {
            this.$emit('delete-filter', filter)
        },

        // FREQ INDICATOR METHODS

        calcMinPath() {
            if (this.cy.elements().nodes().length <= 2) {
                return
            }
            this.totalPath = this.cy.elements().nodes()
            const totalPathLength = this.totalPath.length - 2
            this.minPath = calcMinPathUtil(this.cy, 'START', 'END')
            const minPathNodesCount = this.minPath.filter((item) => item.isNode()).length - 2

            this.minPercent = calcMinimumPercent({
                minPathNodesCount,
                totalPathNodesCount: totalPathLength,
            })
            this.frequencyIndicatorMinValue = this.minPercent
            this.calculateValidPercentages()

            // Start at 100% (index 0) when available.
            this.actualPercentIndex = 0
            this.frequencyIndicatorValue = this.validKnobValues[this.actualPercentIndex] ?? 100
            this.emitFrequencyIndicatorStateChanged()
        },

        calculateValidPercentages() {
            const validPercentages = []
            const percentageToNodesMap = {}
            let currentPath = this.minPath
            let currentNodesCount = currentPath.filter((item) => item.isNode()).length

            percentageToNodesMap[this.minPercent] = [...currentPath.filter((item) => item.isNode()).map((node) => node.data('id'))]
            validPercentages.push(this.minPercent)

            while (currentNodesCount < this.totalPath.length) {
                const nextActivity = this.calcSymmetricDifferencePath(currentPath).nodes().first()
                const prevActivitiesToDraw = []
                const postActivitiesToDraw = []

                // eslint-disable-next-line no-loop-func
                const checkPrevActivities = (node) => {
                    if (!node || node.data('id') === 'START' || node.data('id') === 'END') return
                    if (!currentPath.includes(node) && !prevActivitiesToDraw.includes(node)) {
                        prevActivitiesToDraw.push(node)
                        const prevNode = node?.incomers().nodes().first()
                        if (prevNode) checkPrevActivities(prevNode)
                    }
                }

                // eslint-disable-next-line no-loop-func
                const checkPostActivities = (node) => {
                    if (!node || node.data('id') === 'START' || node.data('id') === 'END') return
                    if (!currentPath.includes(node) && !postActivitiesToDraw.includes(node)) {
                        postActivitiesToDraw.push(node)
                        const nextNode = node?.outgoers().nodes().first()
                        if (nextNode) checkPostActivities(nextNode)
                    }
                }

                checkPrevActivities(nextActivity)
                checkPostActivities(nextActivity)
                const activitiesToDraw = [...new Set([...prevActivitiesToDraw, ...postActivitiesToDraw])]
                currentPath = currentPath.concat(activitiesToDraw)

                currentNodesCount = currentPath.filter((item) => item.isNode()).length

                const newPercent = Math.round(((currentNodesCount) / (this.totalPath.length)) * 100)
                validPercentages.push(newPercent)
                percentageToNodesMap[newPercent] = [...currentPath.filter((item) => item.isNode()).map((node) => node.data('id'))]
            }

            this.validKnobValues = validPercentages.sort((a, b) => b - a)
            this.percentageMap = percentageToNodesMap
        },

        calcSymmetricDifferencePath(path) {
            return this.totalPath.filter((node) => !path.includes(node)).sort((a, b) => a.data('freq') - b.data('freq'))
        },

        removeLessFreqActivities() {
            if (this.actualPercentIndex < this.validKnobValues.length - 1) {
                // eslint-disable-next-line no-plusplus
                this.actualPercentIndex++
                this.frequencyIndicatorValue = this.validKnobValues[this.actualPercentIndex]
                this.drawPercentNodes(this.percentageMap[this.validKnobValues[this.actualPercentIndex]])
                this.emitFrequencyIndicatorStateChanged()
            }
        },

        addLessFreqActivities() {
            if (this.actualPercentIndex > 0) {
                // eslint-disable-next-line no-plusplus
                this.actualPercentIndex--
                this.frequencyIndicatorValue = this.validKnobValues[this.actualPercentIndex]
                this.drawPercentNodes(this.percentageMap[this.validKnobValues[this.actualPercentIndex]])
                this.emitFrequencyIndicatorStateChanged()
            }
        },

        drawPercentNodes(activityNames) {
            this.cy.nodes().forEach((node) => {
                if (!activityNames.includes(node.id())) node.hide()
                else node.show()
            })
        },

        resetFreqIndictadorValues() {
            this.minPath = []
            this.totalPath = []
            this.validKnobValues = []
            this.percentageMap = {}
            this.actualPercentIndex = 0
            this.minPercent = 0
            this.frequencyIndicatorValue = 100
            this.frequencyIndicatorMinValue = 0
            this.emitFrequencyIndicatorStateChanged()
        },

        formatData(data = {}) {
            const activitiesIds = (data.activities || []).reduce((acc, { id, name }) => ({ ...acc, [id]: name }), {})
            const arcs = (data.arcs || []).map(({ source, target, ...rest }) => ({
                ...rest,
                source: activitiesIds[source],
                target: activitiesIds[target],
                sourceId: source,
                targetId: target,
            }))
            return { ...data, arcs }
        },

        async checkData({ variants, variantsUids }) {
            const { processId } = this.$route.params
            const params = this.buildVisualizationParams({
                ...(variants ? { variants } : {}),
                ...(variantsUids ? { variantsUids } : {}),
            })

            if (variantsUids) {
                params.limit = 8
                params.start = 0
                return apiRequest(Api().visualizations.multipleVariants({ processId, params })).catch(notifyError)
            }
            return apiRequest(Api().visualizations.model({ processId, params })).catch(notifyError)
        },
        async openTablePopup() {
            this.isTablePopupVisible = true
        },
    },
}
</script>

<style lang="scss">
.Graph {
    display: block;
    margin: auto;
    width: 100%;
    height: 100%;
}
.title-label {
  font-weight: bold;
  font-size: 14px;
  margin: 0;
}

.frequency-label {
  font-size: 12px;
  color: #666;
  margin: 0;
}
.popup-content {
  position: absolute;
  z-index: 1010;
}
.frequency-indicator {
    display: none;
}
.unrenderable {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(255, 255, 255, 0.8);
    padding: 10px;
    font-size: 18px;
    border: solid 1px;
}
</style>
